import {
  Divider,
  IconButton,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import {
  DownArrowIcon,
  PlusIcon,
  SearchIcon,
  WindowResizeLeftIcon,
} from "components/icons";
import { DeleteConfirmationModal } from "components/modal";
import { useAskHelenaConversations } from "controller";
import dayjs from "dayjs";
import { useAtom, useSetAtom } from "jotai";
import { useMemo } from "react";
import { HelenaSectionIcon } from "shared/features/helena/components/HelenaSectionIcon";
import {
  HelenaLogo,
  HelenaWordmark,
} from "shared/features/helena/components/icons";
import {
  type AiChatHistorySource,
  recordChatOpened,
  trackAiHome,
} from "../../analytics";
import { useChatConversationActions } from "../hooks/useChatConversationActions";
import {
  isChatSearchOpenAtom,
  isChatSidebarOpenAtom,
  isRecentChatsOpenAtom,
} from "../store";
import type { ChatHistoryEntry } from "../types";
import {
  ASK_HELENA_BUCKET_LABELS,
  splitAskHelenaConversations,
  withSessionChat,
} from "../utils/askHelenaConversationList";
import type { ChatRowAction } from "./ChatSidebarItem";
import { ChatSidebarItem } from "./ChatSidebarItem";

/** Placeholder rows while the first page of history loads. */
const SKELETON_ROWS = [0, 1, 2, 3];

/** Kept in sync with the expanded width the layout animates to. */
export const CHAT_SIDEBAR_WIDTH = "206px";

const HISTORY_SOURCE_BY_LABEL: Record<string, AiChatHistorySource> = {
  [ASK_HELENA_BUCKET_LABELS.today]: "recent_today",
  [ASK_HELENA_BUCKET_LABELS.yesterday]: "recent_yesterday",
  [ASK_HELENA_BUCKET_LABELS.previousWeek]: "previous_7_days",
  [ASK_HELENA_BUCKET_LABELS.older]: "older",
};

interface ChatSidebarProps {
  /** Real conversation id, or the pre-id live thread. */
  activeChatId: string | null;
  /** Prepended to "Today" — the thread started in this session, if any. */
  currentSessionChat?: ChatHistoryEntry;
  onSelectConversation: (conversationId: string) => void;
  onNewChat: () => void;
}

export const ChatSidebar = ({
  activeChatId,
  currentSessionChat,
  onSelectConversation,
  onNewChat,
}: ChatSidebarProps) => {
  const theme = useTheme();
  const setIsSidebarOpen = useSetAtom(isChatSidebarOpenAtom);
  const [isRecentOpen, setIsRecentOpen] = useAtom(isRecentChatsOpenAtom);
  const [isSearchOpen, setIsSearchOpen] = useAtom(isChatSearchOpenAtom);

  const { data, isLoading, isError, refetch } = useAskHelenaConversations();
  const actions = useChatConversationActions();

  const { pinned, recent } = useMemo(
    () =>
      withSessionChat(
        splitAskHelenaConversations(data?.items ?? []),
        currentSessionChat,
      ),
    [data?.items, currentSessionChat],
  );

  const handleAction = (chat: ChatHistoryEntry) => (action: ChatRowAction) => {
    switch (action) {
      case "rename":
        actions.startRename(chat.id);
        break;
      case "pin":
        void actions.pin(chat.id);
        break;
      case "unpin":
        void actions.unpin(chat.id);
        break;
      case "delete":
        actions.requestDelete(chat.id);
        break;
    }
  };

  const handleSelect = (
    chat: ChatHistoryEntry,
    source: AiChatHistorySource,
  ) => {
    if (activeChatId !== chat.id) {
      const listed = data?.items.find((item) => item.id === chat.id);

      recordChatOpened();
      trackAiHome("ai_chat_opened_from_history", {
        chat_id: chat.id,
        source,
        chat_age_days: listed
          ? dayjs().diff(dayjs(listed.createdAt), "day")
          : null,
      });
    }

    onSelectConversation(chat.id);
  };

  const renderRow =
    (source: AiChatHistorySource) => (chat: ChatHistoryEntry) => (
      <ChatSidebarItem
        key={chat.id}
        label={chat.title}
        rawTitle={chat.rawTitle}
        isActive={activeChatId === chat.id}
        isPinned={chat.isPinned}
        isRenaming={actions.renamingId === chat.id}
        onClick={() => handleSelect(chat, source)}
        onAction={handleAction(chat)}
        onRename={(title) => void actions.rename(chat.id, title)}
        onCancelRename={actions.cancelRename}
      />
    );

  return (
    <Stack
      sx={{
        // Holds its own width inside the animating wrapper, so the rows stay
        // put while the panel is revealed instead of reflowing.
        width: CHAT_SIDEBAR_WIDTH,
        height: "100%",
        flexShrink: 0,
        minHeight: 0,
        px: "8px",
      }}
    >
      <Stack
        direction="row"
        sx={{ alignItems: "center", gap: "8px", p: "12px 0px 12px 8px" }}
      >
        <Stack
          direction={"row"}
          sx={{
            flex: 1,
            alignItems: "center",
            justifyContent: "flex-start",
            gap: "8px",
          }}
        >
          <HelenaSectionIcon icon={<HelenaLogo />} />
          <HelenaWordmark />
        </Stack>
        <IconButton
          aria-label="Hide chat list"
          onClick={() => setIsSidebarOpen(false)}
          sx={{ color: theme.palette.lightBg.medium }}
        >
          <WindowResizeLeftIcon sx={{ fontSize: "20px" }} />
        </IconButton>
      </Stack>

      <Stack sx={{ gap: "2px", p: "8px 0px 16px 0px" }}>
        {/* New chat is an action, not a destination — it never highlights. */}
        <ChatSidebarItem
          label="New chat"
          icon={<PlusIcon sx={{ fontSize: "24px" }} />}
          onClick={onNewChat}
        />
        {/* Searching is a panel state, not a conversation — it highlights on
            its own and leaves the open chat selected underneath. */}
        <ChatSidebarItem
          label="Search chat"
          icon={<SearchIcon sx={{ fontSize: "24px" }} />}
          isActive={isSearchOpen}
          onClick={() => setIsSearchOpen(true)}
        />
      </Stack>

      <Divider sx={{ borderColor: theme.palette.neutral["010"] }} />

      <Stack
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          gap: "16px",
          p: "16px 0px",
        }}
      >
        {/* No heading until something is pinned — an empty section would read
            as a list that failed to load. */}
        {pinned.length > 0 && (
          <Stack gap="4px">
            <Typography
              variant="button-label"
              sx={{ color: theme.palette.lightBg.high, px: "8px" }}
            >
              Pinned
            </Typography>
            {/* Rendered in the order received: the server sorts most recently
                pinned first, and re-sorting here is what makes pinning an old
                thread look like it did nothing. */}
            {pinned.map(renderRow("pinned"))}
          </Stack>
        )}

        {/* Same rule as Pinned: the heading only earns its place once there is
            something under it. Loading and error still render — hiding those
            would look like the list silently vanished. */}
        {(isLoading || isError || recent.length > 0) && (
          <Stack>
            <Stack
              component="button"
              type="button"
              direction="row"
              onClick={() => setIsRecentOpen((open) => !open)}
              aria-expanded={isRecentOpen}
              sx={{
                alignItems: "center",
                justifyContent: "space-between",
                px: "8px",
                border: "none",
                backgroundColor: "transparent",
                cursor: "pointer",
              }}
            >
              <Typography
                variant="button-label"
                sx={{ color: theme.palette.lightBg.high }}
              >
                Recent
              </Typography>
              <DownArrowIcon
                sx={{
                  fontSize: "18px",
                  color: theme.palette.lightBg.low,
                  transform: isRecentOpen ? "rotate(0deg)" : "rotate(-90deg)",
                  transition: "transform 0.2s",
                }}
              />
            </Stack>

            {isRecentOpen &&
              (isLoading ? (
                SKELETON_ROWS.map((row) => (
                  <Skeleton
                    key={row}
                    variant="text"
                    sx={{ mx: "8px", fontSize: "24px" }}
                  />
                ))
              ) : isError ? (
                <Stack sx={{ gap: "4px", p: "8px" }}>
                  <Typography
                    variant="body-xsmall"
                    sx={{ color: theme.palette.lightBg.low }}
                  >
                    Couldn't load your chats.
                  </Typography>
                  <Typography
                    component="button"
                    type="button"
                    variant="body-xsmall"
                    onClick={() => void refetch()}
                    sx={{
                      alignSelf: "flex-start",
                      p: 0,
                      border: "none",
                      backgroundColor: "transparent",
                      color: theme.palette.primary.tint,
                      cursor: "pointer",
                    }}
                  >
                    Retry
                  </Typography>
                </Stack>
              ) : (
                recent.map((group) => (
                  <Stack key={group.label} sx={{ gap: "2px" }}>
                    <Typography
                      variant="body-xsmall"
                      sx={{ color: theme.palette.lightBg.low, p: "8px" }}
                    >
                      {group.label}
                    </Typography>
                    {group.entries.map(
                      renderRow(
                        HISTORY_SOURCE_BY_LABEL[group.label] ?? "older",
                      ),
                    )}
                  </Stack>
                ))
              ))}
          </Stack>
        )}
      </Stack>

      {/* The delete is soft server-side, but the user has no way back — no
          trash, no restore — so the copy must not promise one. */}
      <DeleteConfirmationModal
        open={!!actions.pendingDeleteId}
        title="Delete chat"
        tagline="This chat and its messages will be removed. This can't be undone."
        isLoading={actions.isDeleting}
        onClose={actions.cancelDelete}
        onPrimaryButtonClick={() => void actions.confirmDelete()}
        onSecondaryButtonClick={actions.cancelDelete}
      />
    </Stack>
  );
};
